import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment.development';
import { GetUsersResponseType, Farm, Employee, Product, UploadImageFileRequestBody, AddProductResponse, Message, GetFarmImagesResponseType, UpdateProductRequestBody, Event, Organisation } from '../../models/types';
import { jwtDecode } from 'jwt-decode'; // Ensure this import is correct
import { LoginService } from './login.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UpdateEmployeeModalComponent } from '../components/update-employee-modal/update-employee-modal.component';
import { UpdateProductModalComponent } from '../components/update-product-modal/update-product-modal.component';
import { AddProductModalComponent } from '../components/add-product-modal/add-product-modal.component';
import { AddEmployeeModalComponent } from '../components/add-employee-modal/add-employee-modal.component';
import { ContactFarmModalComponent } from '../components/contact-farm-modal/contact-farm-modal.component';
import { EmployeeDetailsModalComponent } from '../components/employee-details-modal/employee-details-modal.component';
import { AllEventsModalComponent } from '../components/all-events-modal/all-events-modal.component';
import { EventApi } from '@fullcalendar/core';
import { MessageDetailsComponentComponent } from '../components/message-details-component/message-details-component.component';
import { SendAdminMessageModalComponent } from '../components/send-admin-message-modal/send-admin-message-modal.component';
import { AddFarmImagesModalComponent } from '../add-farm-images-modal/add-farm-images-modal.component';
import { EventDetailsModalComponent } from '../components/event-details-modal/event-details-modal.component';
import { AddOrgImagesModalComponent } from '../components/add-org-images-modal/add-org-images-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ProfilePageService {

  private token: string | null = null;
  onUserLoggedIn: EventEmitter<any> = new EventEmitter<any>();
  onUserLoggedOut: EventEmitter<any> = new EventEmitter<any>();
  private modalRef: NgbModalRef | null = null;

  constructor(
    private http: HttpClient, 
    private loginService: LoginService, 
    private modalService: NgbModal) { }

  getUserProfile(userID: number): Observable<HttpResponse<GetUsersResponseType>> {
    return this.http.get<GetUsersResponseType>(environment.getUserInfoURL, {
      params: { userID: userID.toString() },
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  getUserEmail(userID: number): Observable<HttpResponse<any>> {
    return this.http.get<any>(environment.getUserEmailURL, {
      params: { userID },
      observe: 'response',
    });
  }

  updateUserProfile(user: GetUsersResponseType): Observable<any> {
    return this.http.post(environment.updateUserInfoURL, user, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  testTokenRequired(userID: number): Observable<any> {
    return this.http.get<GetUsersResponseType>(environment.getUserInfoURL, {
      params: { userID: userID.toString() },
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  getFarmInfo(userID: number): Observable<HttpResponse<Farm>> {
    return this.http.get<Farm>(environment.getFarmInfoURL, {
      params: { userID: userID},
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  updateFarmInfo(Farm: Farm): Observable<any> {
    return this.http.put(environment.updateFarmInfoURL, Farm, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  addEmployee(farmID: number, employee: Employee){
    return this.http.put(environment.addEmployeeURL, {farmID, employee}, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  deleteEmployee(farmID: number, employeeID: number): Observable<HttpResponse<any>> {
    const url = `${environment.addEmployeeURL}/${farmID}/${employeeID}`;
    return this.http.delete<any>(url, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response',
    });
  }

  updateEmployee(employee: Employee){
    return this.http.put(environment.updateEmployeeURL, {employee}, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  addProduct(farmID: number, product: Product): Observable<AddProductResponse> {
      return this.http.put<AddProductResponse>(environment.productURL, {farmID, product}, {
        headers: {
          ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
        }
      });
    }

  deleteProduct(farmID: number, productID: number): Observable<HttpResponse<any>> {
    const url = `${environment.productURL}/${farmID}/${productID}`;
    return this.http.delete<any>(url, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response',
    });
  }

  updateProduct(product: Product){
    return this.http.put(environment.updateProductURL, {product}, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  updateProductWithImage(data: UpdateProductRequestBody): Observable<any> {
    const formData = new FormData();
    formData.append('product', JSON.stringify(data.product));
    formData.append('picture', data.picture);
    console.log('formData:', formData);
    return this.http.put(environment.updateProductWithImageURL, formData, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! }),
      },
      observe: 'response',
    });
  }

  uploadImageFile(data: UploadImageFileRequestBody): Observable<any> {
    const formData = new FormData();
    formData.append("picture", data.picture);
    formData.append("productID", data.productID.toString());
    console.log('formData:', formData);
    return this.http.post(environment.productURL + "/upload_picture", formData, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: "response"
    })
  }

  getFarmImages(farmID: number): Observable<HttpResponse<GetFarmImagesResponseType>> {
    return this.http.get<GetFarmImagesResponseType>(environment.getFarmImagesURL, {
      params: { farmID: farmID.toString() },
      observe: 'response',
    });
  }

  addFarmImages(farmID: number, images: File[]): Observable<any> {
    const formData = new FormData();
    formData.append("farmID", farmID.toString());
    images.forEach((image) => {
      formData.append("images", image);
    });
    return this.http.put(environment.getFarmImagesURL, formData, {
      headers: {
        ...(this.loginService.IsValidToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  deleteFarmImage(imageRef: string): Observable<HttpResponse<any>> {
    return this.http.delete<any>(environment.getFarmImagesURL, {
      params: { imageRef },
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response',
    });
  }

  getFarmInfoForUser(farmId: number): Observable<HttpResponse<Farm>> {
    return this.http.get<Farm>(environment.getFarmInfoUserURL, {
      params: { farmId },
      observe: 'response',
    });
  }

  deleteMessage(message: Message): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${environment.deleteMessageURL}/${message.messageID}`, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response',
    });
  }
  
  deleteSubscriber(userID: number): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${environment.deleteSubscriberURL}/${userID}`, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response',
    });
  }

  //Modal service for updating employee
  open(employee: Employee) {
    this.modalRef = this.modalService.open(UpdateEmployeeModalComponent, { centered: true });
    this.modalRef.componentInstance.employee = employee; // Pass data to modal component
  }

  close() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
  }

  openProduct(product: Product) {
    this.modalRef = this.modalService.open(UpdateProductModalComponent, { centered: true });
    this.modalRef.componentInstance.product = product; // Pass data to modal component
  }

  openProductAdd(farm: Farm) {
    this.modalRef = this.modalService.open(AddProductModalComponent, { centered: true });
    this.modalRef.componentInstance.farm = farm; // Pass data to modal component
  }

  openEmployeeAdd(farm: Farm) {
    this.modalRef = this.modalService.open(AddEmployeeModalComponent, { centered: true });
    this.modalRef.componentInstance.farm = farm; // Pass data to modal component
  }

  openContactFarmModal(farm: Farm) {
    this.modalRef = this.modalService.open(ContactFarmModalComponent, { centered: true });
    this.modalRef.componentInstance.farm = farm; // Pass data to modal component
  }

  openEmployeeDetailsModal(employee: Employee) {
    this.modalRef = this.modalService.open(EmployeeDetailsModalComponent, { centered: true });
    this.modalRef.componentInstance.employee = employee; // Pass data to modal component
  }

  openAllEventsModal(events: EventApi[]) {
    this.modalRef = this.modalService.open(AllEventsModalComponent, { centered: true });
    this.modalRef.componentInstance.events = events; // Pass data to modal component
  }

  openMessageDetailsModal(message: Message): void {
    this.modalRef = this.modalService.open(MessageDetailsComponentComponent, { centered: true });
    this.modalRef.componentInstance.message = message;
  }

  openAdminMessageModal(farmID: number) {
    this.modalRef = this.modalService.open(SendAdminMessageModalComponent, { centered: true });
    this.modalRef.componentInstance.farmID = farmID;
  }

  openImageAddModal(farm: Farm) {
    this.modalRef = this.modalService.open(AddFarmImagesModalComponent, { centered: true });
    this.modalRef.componentInstance.farm = farm; // Pass data to modal component
  }

  openOrgImageAddModal(organisation: Organisation) {
    this.modalRef = this.modalService.open(AddOrgImagesModalComponent, { centered: true });
    this.modalRef.componentInstance.organisation = organisation; // Pass data to modal component
  }

  openEventDetailsModal(event: Event) {
    this.modalRef = this.modalService.open(EventDetailsModalComponent, { centered: true, size: 'lg' });
    this.modalRef.componentInstance.event = event;
  }
}
